import { z } from 'zod';

/**
 * The server-authored transition vocabulary for a document element.
 *
 * A document names an *intent* and nothing else; the client host resolves that
 * intent to a duration, an easing curve, and a driver, and owns reduce-motion
 * suppression. The contract lives here rather than in the design system because
 * both the authoring side (`apps/api`) and the rendering side
 * (`packages/design-system`) must see it, and an api workspace may not depend on
 * a ui package (ADR-001). The intent → mechanics mapping stays client-side, next
 * to the design tokens it reads.
 *
 * Enum values are shape/role descriptors, never a use case or domain concept
 * (ADR-020 Rule 4).
 */
export const motionEnterSchema = z.enum(['fade_in', 'fade_slide_up', 'bounce', 'none']);

export const motionExitSchema = z.enum(['fade_out', 'fade_slide_down', 'none']);

export const motionPropsSchema = z
  .object({
    enter: motionEnterSchema.optional(),
    exit: motionExitSchema.optional(),
    delayMs: z.number().int().min(0).max(10000).optional(),
    staggerIndex: z.number().int().min(0).max(20).optional(),
    staggerMs: z.number().int().min(0).max(300).optional(),
    testID: z.string().min(1).optional(),
    /**
     * Replay the entrance each time the member returns to the screen that
     * hosts the element (back from a step pushed over it), not only on first
     * mount. Absent or false keeps the mount-once behaviour. Reduced-motion
     * suppression applies to a replay exactly as to the first play.
     */
    replayOnReturn: z.boolean().optional(),
  })
  // Strictness is the enforcement: a document that authors `durationMs`,
  // `easing`, or a driver choice is rejected rather than partially honoured.
  .strict();

export type MotionEnter = z.infer<typeof motionEnterSchema>;
export type MotionExit = z.infer<typeof motionExitSchema>;
export type MotionProps = z.infer<typeof motionPropsSchema>;
